import cx from 'classnames';
import * as _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';
import type { InjectedRouter } from 'react-router';

import {
  BINARY_DECOMPOSITION,
  DISABLE_MANUAL_DEPENDENCIES_REVAMP,
  VENDORED_DEPENDENCY_DETECTION,
} from '../../../../shared/featureFlags/flagNames';
import { BinaryFetcherName } from '../../../../shared/fetchers';
import { plural } from '../../../../shared/helpers';
import Locator from '../../../../shared/Locator';
import { DependencySource } from '../../../../shared/vendoredDependencies';
import { ProjectSource } from '../../../../typings/enums';
import { useDresser } from '../../../contexts/DrawerContext';
import { window } from '../../../globals';
import { useIsFeatureEnabled } from '../../../helpers/features';
import { useOnUnmount } from '../../../hooks';
import { useReleaseGroupAllProjects } from '../../../hooks/api/releaseGroups';
import useRevision from '../../../hooks/api/revisions/useRevision';
import * as DependenciesV2Store from '../../../redux/dependenciesV2';
import AddDependencyDrawer from '../../drawers/AddDependencyDrawer';
import PaginationFooter from '../../generic/Pagination/PaginationFooter';
import { SidebarLayout } from '../../layouts';
import ModalManagePackageLabels from '../../modals/ModalManagePackageLabels';
import ModalRemoveDependency from '../../modals/ModalRemoveDependency';
import useModalRemoveDependency from '../../modals/ModalRemoveDependency/useModalRemoveDependency';
import {
  AnalyzedWithoutSparkleCard,
  shouldShowAnalyzedWithoutSparkleCard,
} from '../AnalyzedWithoutSparkleCard/AnalyzedWithoutSparkleCard';
import { InventoryViewHeader } from '../inventory/InventoryViewHeader/InventoryViewHeader';
import type { ProjectGroup, ProjectGroupRelease } from '../../../../shared/model-typings';
import type { ManuallyAddedDependencyRequestKind } from '../../../../typings/api';
import type { IAsync, Project, RouterProps } from '../../../types';

import DependenciesTable from './DependenciesTable';
import DependencyFilters from './filters';
import DependenciesHeader from './header';
import { useDependencyDrawer } from './hooks/useDependencyDrawer';
import useDependencyModals from './hooks/useDependencyModals';
import useDependencyMode from './hooks/useDependencyMode';
import useFetchDependencyAction from './hooks/useFetchDependencyAction';
import { useSetDependencyScope } from './hooks/useSetDependenciesScope';
import { useSyncStateAndURLParams } from './hooks/useSyncStateAndURLParams';
import AddDependency from './modals/AddDependency';
import BinaryMatchDetailsModalContainer from './modals/BinaryMatchDetailsModal';
import EditDependency from './modals/EditDependency';
import StreamBuild from './modals/StreamBuild';
import ViewRelationships from './modals/ViewRelationships';
import VendoredDependenciesLayout from './VendoredDependenciesLayout';

import styles from './dependencies.module.less';

const countOptions = [
  25,
  50,
  75,
  100,
];

function summarizePath(path: string): string {
  if (!path || path === '/') {
    return 'this project';
  }
  return path;
}

export type DependenciesV2Props = {
  router: InjectedRouter;
  project?: IAsync<Project>;
  projectGroup?: ProjectGroup;
  projectGroupRelease?: ProjectGroupRelease;
  onProjectChange?: () => void;
} & RouterProps;

const DependenciesV2: VoidFunctionComponent<DependenciesV2Props> = ({
  router,
  location,
  params,
  project,
  projectGroup,
  projectGroupRelease,
  onProjectChange = _.noop,
}) => {
  const dispatch = useDispatch();
  useSyncStateAndURLParams({ router, location, params });
  const modals = useDependencyModals();

  const { project_id: projectId, revision_id: revisionId } = params;
  const scope = useSelector(DependenciesV2Store.selectors.getScope);
  const isBinaryDecompEnabled = useIsFeatureEnabled(BINARY_DECOMPOSITION);

  const revision = useRevision(`${projectId}$${revisionId}`, !!revisionId);
  const doesRevisionHaveSparkleAnalysis = !!revision?.data?.metadata?.latestSparkleAnalysis;
  const revisionAnalyzedAt = revision?.data?.dependency_cache_valid;

  // optional projectRevision used by the EditDependency modal
  const projectRevision = scope && scope.scope === 'project' ? scope.revision : undefined;
  const releaseGroupId = scope && scope.scope === 'releaseGroup' ? scope.projectGroupId : -1;

  const isContainerProject = (project && project?.data?.type === ProjectSource.Container) ?? false;
  const isBinaryProject = (project && project?.data?.type === ProjectSource.Binary) ?? false;

  const isReleaseGroupScope = useSelector(DependenciesV2Store.selectors.getIsReleaseGroupScope);
  const isVendoredDepsEnabled = useIsFeatureEnabled(VENDORED_DEPENDENCY_DETECTION);
  const dependencyMode = useDependencyMode();
  const isVendoredView = dependencyMode === DependencySource.Vendored;
  const {
    data: releaseProjects,
  } = useReleaseGroupAllProjects(releaseGroupId, isBinaryDecompEnabled && releaseGroupId > 0);
  const isReleaseGroupWithBinaryProject = (
    isReleaseGroupScope &&
    releaseProjects?.some(releaseProject => {
      return Locator.parse(releaseProject).fetcher === BinaryFetcherName;
    })) ?? false;

  const filters = useSelector(DependenciesV2Store.selectors.getFilters);
  const page = useSelector(DependenciesV2Store.selectors.getPage);
  const count = useSelector(DependenciesV2Store.selectors.getCount);
  const totalItems = useSelector(DependenciesV2Store.selectors.getTotalDependencies);

  const setPage = useFetchDependencyAction(DependenciesV2Store.actions.setPage);
  const setCount = useFetchDependencyAction(DependenciesV2Store.actions.setCount);

  // used by the dependency drawer
  const [selectedDependencyRevisionLocator, setSelectedDependencyRevisionLocator] = useState<string | null>(null);
  // used by the remove dependency modal
  const { dependencyToRemove, setDependencyToRemove, removeDependency } = useModalRemoveDependency();
  // used to overwrite user dependencies
  const [dependencyLocatorToOverwrite, setDependencyLocatorToOverwrite] = useState<string | null>(null);
  // used by edit dependency component - see: https://github.com/fossas/FOSSA/pull/16861
  const [dependencyLocatorToEdit, setDependencyLocatorToEdit] = useState<string | null>(null);
  // used by the add dependency component
  const [manualDependencyAddMode, setManualDependencyAddMode] = (
    useState<ManuallyAddedDependencyRequestKind | null>(null));
  // used to view in progress builds
  const [inProgressBuildId, setInProgressBuildId] = useState<number | null>(null);

  const onRemoveProjectDependency = useCallback((dependencyRevisionLocator: string) => {
    // Close the drawer and reset the selected dependency revision locator
    setSelectedDependencyRevisionLocator(null);
    // And reset the dependency to remove
    setDependencyToRemove(null);
    // Remove the dependency from the watch list to prevent unnecessary builds
    dispatch(DependenciesV2Store.actions.removeRevisionFromBuildWatch(dependencyRevisionLocator));
    // Trigger a project reload to refresh the dependencies on the page
    onProjectChange();
  }, [setSelectedDependencyRevisionLocator, dispatch, onProjectChange, setDependencyToRemove]);

  useDependencyDrawer({
    scope,
    selectedDependencyRevisionLocator,
    setSelectedDependencyRevisionLocator,
    onDeleteProjectDependency: onRemoveProjectDependency,
  });

  const isManualDependenciesRevampEnabled = !useIsFeatureEnabled(DISABLE_MANUAL_DEPENDENCIES_REVAMP);
  const { pushDrawer, popDrawer } = useDresser();
  const handleAddManualDependency = () => {
    if (isManualDependenciesRevampEnabled && scope?.scope === 'project') {
      pushDrawer(
        <AddDependencyDrawer
          userRevisionLocator={scope.revision}
          onClose={popDrawer}
          isVendored={isVendoredView}
        />,
      );
    } else {
      setManualDependencyAddMode('new');
    }
  };
  const handleOverwriteManualDependency = (overwriteDependencyLocator: string) => {
    if (isManualDependenciesRevampEnabled && scope?.scope === 'project') {
      pushDrawer(
        <AddDependencyDrawer
          userRevisionLocator={scope.revision}
          overwriteDependencyLocator={overwriteDependencyLocator}
          onClose={popDrawer}
          isVendored={isVendoredView}
        />,
      );
    } else {
      setDependencyLocatorToOverwrite(overwriteDependencyLocator);
      setManualDependencyAddMode('replace');
    }
  };

  // If the current user is authenticated, check if they belong to the current scope target's org.
  useEffect(() => {
    if (window.user) {
      // Check if the user is logged in and belongs to the project's org.
      if (project && window.user.organizationId === project.data?.organizationId) {
        dispatch(DependenciesV2Store.actions.setIsOrgMember(true));
      }
      // Check if the user is logged in and belongs to the project group's org.
      if (projectGroup && window.user.organizationId === projectGroup.organizationId) {
        dispatch(DependenciesV2Store.actions.setIsOrgMember(true));
      }
    }
  }, [dispatch, project, projectGroup]);

  useSetDependencyScope({
    projectId,
    revisionId,
    projectGroup,
    projectGroupRelease,
  });

  useOnUnmount(() => {
    dispatch(DependenciesV2Store.actions.resetState());
  });

  // Use tree layout for project-scoped vendored view; projectRevision gates on data readiness
  // (tree needs a revision to query vendored paths from)
  const shouldUseVendoredDepsTreeLayout =
    isVendoredView && !isReleaseGroupScope && isVendoredDepsEnabled && projectRevision;

  return (
    <div className={cx('container', 'container-responsive', styles.DependenciesContainer)}>
      { scope?.scope === 'project' && !doesRevisionHaveSparkleAnalysis
        && shouldShowAnalyzedWithoutSparkleCard(revisionAnalyzedAt) && (
        <AnalyzedWithoutSparkleCard revisionAnalyzedAt={revisionAnalyzedAt} />
      ) }
      <InventoryViewHeader
        title={isVendoredView ? 'Vendored Dependencies' : 'Dependencies'}
        label={shouldUseVendoredDepsTreeLayout ? (
          <span className={styles.DependenciesViewHeaderLabel}>
            {totalItems} vendored {plural(totalItems, 'dependency', 'dependencies')} found in <span className="bold">{summarizePath(filters.vendoredPath ?? '/')}</span>
          </span>
        ) : undefined}
      />
      <SidebarLayout>
        <SidebarLayout.Header>
          <DependenciesHeader
            handleAddManualDependency={handleAddManualDependency}
            depModals={modals}
          />
        </SidebarLayout.Header>
        <SidebarLayout.Main>
          {shouldUseVendoredDepsTreeLayout ? (
            <VendoredDependenciesLayout
              revisionLocator={projectRevision}
              modals={modals}
              setSelectedDependencyRevisionLocator={setSelectedDependencyRevisionLocator}
              removeDependency={removeDependency}
              setDependencyLocatorToEdit={setDependencyLocatorToEdit}
              setInProgressBuildId={setInProgressBuildId}
              handleOverwriteManualDependency={handleOverwriteManualDependency}
            />
          ) : (
            <DependenciesTable
              modals={modals}
              removeDependency={removeDependency}
              setSelectedDependencyRevisionLocator={setSelectedDependencyRevisionLocator}
              setInProgressBuildId={setInProgressBuildId}
              setDependencyLocatorToEdit={setDependencyLocatorToEdit}
              handleOverwriteManualDependency={handleOverwriteManualDependency}
            />
          )}
          <PaginationFooter
            page={page}
            count={count}
            totalItems={totalItems}
            setPage={setPage}
            setCount={setCount}
            countOptions={countOptions}
          />
        </SidebarLayout.Main>
        <SidebarLayout.Sidebar>
          <DependencyFilters
            shouldDisplayConfidenceFilter={isBinaryProject || isReleaseGroupWithBinaryProject}
            isContainerProject={isContainerProject}
            isReleaseGroupScope={isReleaseGroupScope}
            isVendoredView={isVendoredView}
          />
        </SidebarLayout.Sidebar>
      </SidebarLayout>
      <StreamBuild buildId={inProgressBuildId} setBuildId={setInProgressBuildId} />
      {/*
        TODO: Remove EditDependency once we resolve the related customer issue,
        see: https://github.com/fossas/FOSSA/pull/16861
      */}
      <EditDependency
        userRevisionLocator={projectRevision}
        dependencyLocatorToEdit={dependencyLocatorToEdit}
        setDependencyLocatorToEdit={setDependencyLocatorToEdit}
      />
      {/* The following modals only apply to project dependencies. */}
      {scope && scope.scope === 'project' && (
        <>
          <ViewRelationships projectRevision={scope.revision} modals={modals} />
          <AddDependency
            userRevisionLocator={scope.revision}
            manualDependencyAddMode={manualDependencyAddMode}
            setManualDependencyAddMode={setManualDependencyAddMode}
            dependencyLocatorToOverwrite={dependencyLocatorToOverwrite}
            setDependencyLocatorToOverwrite={setDependencyLocatorToOverwrite}
          />
          <ModalRemoveDependency
            userRevisionLocator={scope.revision}
            dependencyToRemove={dependencyToRemove}
            setDependencyToRemove={setDependencyToRemove}
            onRemoveDependency={onRemoveProjectDependency}
          />
          <ModalManagePackageLabels
            show={modals.managePackageLabelsModal.isOpen}
            onHide={modals.managePackageLabelsModal.onClose}
            context={modals.managePackageLabelsModal.context}
            parentRevisionLocator={projectRevision}
            isGlobalEnabled
            isProjectEnabled
            isRevisionEnabled
          />
          <BinaryMatchDetailsModalContainer modals={modals} projectRevision={scope.revision} />
        </>
      )}
    </div>
  );
};

export default DependenciesV2;
